import React, { createContext, useContext, useEffect, useState } from "react";

// KINOS Helpdesk — English + Tetum dictionaries.
// Keep this file the single source of truth for user-facing UI strings.
// Only labels/CTAs are translated. User-generated content (ticket titles, comments)
// stays in whatever language the customer wrote it in.

export const DICTS = {
  en: {
    // Nav & shell
    nav_workspace: "Workspace",
    nav_dashboard: "Dashboard",
    nav_tickets: "Tickets",
    nav_board: "Board",
    nav_customers: "Customers",
    nav_kb: "Knowledge Base",
    nav_hof: "Hall of Fame",
    nav_profile: "My Profile",
    nav_trash: "Trash",
    nav_admin: "Admin",
    new_ticket: "New Ticket",
    sign_out: "Sign out",
    switch_account: "Sign out to switch account",
    end_user_hint: "You're signed in as an END USER. Board, Trash, Admin, delete icons, split composer and bulk-assign are only available to staff / admin accounts.",
    role_end_user: "END USER",
    // Common
    loading: "Loading...",
    search: "Search",
    save: "Save",
    cancel: "Cancel",
    // Dashboard
    dash_command_center: "Command Center",
    dash_hello: "Good to see you, {name}.",
    dash_intro: "Live view of KINOS IT tickets, SLA health, and team throughput.",
    dash_loading: "Loading dashboard...",
    kpi_total: "Total",
    kpi_open: "Open",
    kpi_pending: "Pending",
    kpi_resolved: "Resolved",
    kpi_sla_breached: "SLA Breached",
    kpi_csat: "CSAT",
    kpi_low_ratings: "Low Ratings",
    kpi_ratings_suffix: "{n} ratings",
    kpi_awaiting_reply: "{n} awaiting reply",
    kpi_at_most_2: "≤ 2 stars",
    low_ratings_header: "Low Customer Ratings · ≤ 2 stars · needs a call",
    callback_assigned: "Callback assigned",
    needs_callback: "Needs callback",
    avg_first_response: "Avg first response",
    avg_resolution: "Avg resolution",
    // Tickets list
    tickets_queue: "Queue",
    tickets_heading: "All Tickets",
    tickets_export_csv: "Export CSV",
    tickets_monthly_report: "Monthly Report",
    tickets_type_all: "All",
    tickets_type_customer: "Customer",
    tickets_type_internal: "Internal",
    tickets_search_ph: "Search Kinos###/YY, title, organization, assignee, collaborator...",
    tickets_filter: "Filter",
    tickets_all_status: "All Status",
    tickets_all_priority: "All Priority",
    tickets_priority_low: "Low",
    tickets_priority_medium: "Medium",
    tickets_priority_high: "High",
    tickets_priority_critical: "Critical",
    tickets_selected: "{n} selected",
    // Customers
    customers_directory: "Directory",
    customers_heading: "Customers",
    customers_intro: "Everyone who has raised (or will raise) a ticket with KINOS IT Support.",
    customers_new_btn: "New customer",
    customers_create_title: "Create customer profile",
    customers_mark_vip: "Mark as VIP",
    customers_save_btn: "Save customer",
    customers_search_ph: "Search name, email, phone, organization…",
    customers_vip_only: "VIP only",
    customers_records_n: "{n} record",
    customers_records_p: "{n} records",
    customers_empty_title: "No customer profiles yet.",
    customers_empty_hint: "Click New customer to add one.",
    customers_ticket_n: "{n} ticket",
    customers_ticket_p: "{n} tickets",
    customers_open_suffix: "{n} open",
    // Admin
    admin_area: "Admin",
    admin_heading: "Workspace controls",
    admin_tab_users: "Users",
    admin_tab_categories: "Categories",
    admin_tab_replies: "Saved Replies",
    admin_tab_assets: "Assets",
    admin_tab_inbound: "Inbound Email",
    // Ticket detail
    td_requested_by: "Requested by",
    td_assigned_engineer: "Assigned engineer",
    td_support_group: "Support group",
    td_unassigned: "Unassigned",
    td_performing_task: "performing task",
    td_awaiting_assignment: "awaiting assignment",
    td_general_it: "General IT",
    td_collaborators: "Collaborators",
    td_auto_triaged: "Auto-triaged",
    td_by: "By",
    td_min_logged: "{n}m logged",
    td_callback_of: "This is a low-rating callback task for a resolved customer ticket.",
    td_open_original: "Open the original ticket",
    td_has_callback: "A callback task was auto-created for this low rating.",
    td_open_callback: "Open the callback ticket",
    // Composer
    td_internal_header: "Troubleshoot log · team only",
    td_internal_placeholder: "Log troubleshooting steps, findings, or status updates for your team (never sent to the customer). Type @name to loop in a colleague.",
    td_internal_hint: "Team-only progress log · not visible to the customer · never emailed.",
    td_public_header: "Public reply · sent to customer",
    td_public_placeholder: "Reply to the customer — this is delivered to their inbox and shown on their portal.",
    td_email_customer: "Email {email} when posted",
    td_email_default: "(on by default)",
    td_mention_teammate: "Mention teammate",
    td_saved_for: "For {cat}:",
    td_team_replies: "Team:",
    td_my_snippets: "My snippets:",
    td_translate: "Translate",
    td_translating: "Translating...",
    td_hide_translation: "Hide translation",
    td_translation_label: "Translation",
    td_ai_draft: "AI Draft",
    td_ai_drafting: "Drafting...",
    td_time_min: "Time (min)",
    td_post: "Post",
    td_post_log: "Post troubleshoot log",
    td_post_log_and_email: "Email reply & post troubleshoot log",
    td_post_and_email: "Post & email customer",
    // Login
    login_strap_top: "KINOS · Ticket Management System",
    login_headline_line1: "Tickets in.",
    login_headline_line2: "Solutions out.",
    login_tagline: "A smart and reliable IT helpdesk platform built for organizations in Timor-Leste. Manage support requests, assign incidents to the right technicians, track SLA performance, and let AI-powered pre-triage prioritize issues faster.",
    login_strap_bottom: "KINOS Ticket Management System — faster support, clearer communication, better service.",
    login_signin: "Sign In",
    login_access_workspace: "Access your workspace",
    login_subtitle: "Continue with your Google account. Only pre-approved emails can access KINOS Helpdesk — ask your admin to add you first.",
    login_continue_google: "Continue with Google",
    login_feature_ai: "AI auto-categorization & priority pre-triage",
    login_feature_sla: "Business-hours SLA countdowns & breach alerts",
    login_feature_roles: "Role-based portals · Customer · Agent · Team Leader · Admin",
    // Language switcher
    lang_switcher_label: "Language",
    lang_en: "English",
    lang_tet: "Tetum",
  },
  tet: {
    // Nav & shell
    nav_workspace: "Fatin Servisu",
    nav_dashboard: "Painél",
    nav_tickets: "Bilhete sira",
    nav_board: "Kuadru",
    nav_customers: "Kliente sira",
    nav_kb: "Baze Konesimentu",
    nav_hof: "Hall of Fame",
    nav_profile: "Ha'u-nia Perfil",
    nav_trash: "Lixera",
    nav_admin: "Administrasaun",
    new_ticket: "Bilhete Foun",
    sign_out: "Sai",
    switch_account: "Sai atu troka konta",
    end_user_hint: "Ita tama nu'udar UTILIZADOR SIMPLES. Kuadru, Lixera, Administrasaun, ícone hamos, split composer no atribui-massa só disponivel ba konta staff / admin.",
    role_end_user: "UTILIZADOR SIMPLES",
    // Common
    loading: "Karrega hela...",
    search: "Buka",
    save: "Salva",
    cancel: "Kansela",
    // Dashboard
    dash_command_center: "Sentru Komandu",
    dash_hello: "Kontente atu haree ita, {name}.",
    dash_intro: "Vista dadaun kona-ba bilhete KINOS IT, saúde SLA, no dezempeñu ekipa nian.",
    dash_loading: "Karrega hela painél...",
    kpi_total: "Total",
    kpi_open: "Loke",
    kpi_pending: "Espera",
    kpi_resolved: "Rezolve tiha",
    kpi_sla_breached: "SLA La Kumpri",
    kpi_csat: "CSAT",
    kpi_low_ratings: "Avaliasaun Aas Ki'ik",
    kpi_ratings_suffix: "{n} avaliasaun",
    kpi_awaiting_reply: "{n} hein resposta",
    kpi_at_most_2: "≤ 2 fitun",
    low_ratings_header: "Avaliasaun Kliente Ki'ik · ≤ 2 fitun · presiza telefona",
    callback_assigned: "Telefonema atribui tiha",
    needs_callback: "Presiza telefona",
    avg_first_response: "Média resposta dahuluk",
    avg_resolution: "Média rezolusaun",
    // Tickets list (Tetum)
    tickets_queue: "Fila",
    tickets_heading: "Bilhete Hotu",
    tickets_export_csv: "Ezporta CSV",
    tickets_monthly_report: "Relatóriu Fulan-Fulan",
    tickets_type_all: "Hotu",
    tickets_type_customer: "Kliente",
    tickets_type_internal: "Internu",
    tickets_search_ph: "Buka Kinos###/YY, títulu, organizasaun, atribui, kolaborador...",
    tickets_filter: "Filtru",
    tickets_all_status: "Estatus Hotu",
    tickets_all_priority: "Prioridade Hotu",
    tickets_priority_low: "Baixu",
    tickets_priority_medium: "Médiu",
    tickets_priority_high: "Aas",
    tickets_priority_critical: "Krítiku",
    tickets_selected: "{n} hili tiha",
    // Customers (Tetum)
    customers_directory: "Diretóriu",
    customers_heading: "Kliente sira",
    customers_intro: "Ema hotu ne'ebé hato'o (ka sei hato'o) bilhete ba KINOS IT Support.",
    customers_new_btn: "Kliente Foun",
    customers_create_title: "Kria perfil kliente",
    customers_mark_vip: "Marka nu'udar VIP",
    customers_save_btn: "Salva kliente",
    customers_search_ph: "Buka naran, email, telefone, organizasaun…",
    customers_vip_only: "VIP de'it",
    customers_records_n: "{n} rejistu",
    customers_records_p: "{n} rejistu",
    customers_empty_title: "Seidauk iha perfil kliente.",
    customers_empty_hint: "Klik iha Kliente Foun atu aumenta ida.",
    customers_ticket_n: "{n} bilhete",
    customers_ticket_p: "{n} bilhete",
    customers_open_suffix: "{n} loke",
    // Admin (Tetum)
    admin_area: "Administrasaun",
    admin_heading: "Kontrolu Fatin Servisu",
    admin_tab_users: "Utilizador sira",
    admin_tab_categories: "Kategoria sira",
    admin_tab_replies: "Resposta Salva",
    admin_tab_assets: "Ativu sira",
    admin_tab_inbound: "Email Tama",
    // Ticket detail (Tetum)
    td_requested_by: "Ne'ebé husu",
    td_assigned_engineer: "Inzeñeiru atribui",
    td_support_group: "Grupu apoiu",
    td_unassigned: "Seidauk atribui",
    td_performing_task: "servisu hela",
    td_awaiting_assignment: "hein atribuisaun",
    td_general_it: "IT Jerál",
    td_collaborators: "Kolaboradór",
    td_auto_triaged: "Triage-automátiku",
    td_by: "Husi",
    td_min_logged: "{n}m rejistadu",
    td_callback_of: "Ne'e tarefa telefonema ba bilhete kliente ne'ebé rezolvidu tiha.",
    td_open_original: "Loke bilhete orijinál",
    td_has_callback: "Ami kria tarefa telefonema automátikamente tanba avaliasaun aas ki'ik.",
    td_open_callback: "Loke bilhete telefonema",
    // Composer (Tetum)
    td_internal_header: "Rejistu troubleshoot · ekipa de'it",
    td_internal_placeholder: "Rejistu pasu troubleshoot, deskoberta, ka atualizasaun status ba ita-nia ekipa (nunka haruka ba kliente). Hakerek @naran atu bolu kolega ida.",
    td_internal_hint: "Rejistu progresu ekipa de'it · kliente la haree · nunka haruka email.",
    td_public_header: "Resposta públiku · haruka ba kliente",
    td_public_placeholder: "Responde ba kliente — sei entrega ba email no aparese iha portál.",
    td_email_customer: "Haruka email ba {email} bainhira publika",
    td_email_default: "(on husi inisiu)",
    td_mention_teammate: "Bolu kolega ekipa",
    td_saved_for: "Ba {cat}:",
    td_team_replies: "Ekipa:",
    td_my_snippets: "Ha'u-nia snippet sira:",
    td_translate: "Tradús",
    td_translating: "Tradús hela...",
    td_hide_translation: "Kaer tradusaun",
    td_translation_label: "Tradusaun",
    td_ai_draft: "Rascunhu AI",
    td_ai_drafting: "Rascunhu hela...",
    td_time_min: "Tempu (min)",
    td_post: "Publika",
    td_post_log: "Publika rejistu troubleshoot",
    td_post_log_and_email: "Haruka resposta & publika rejistu",
    td_post_and_email: "Publika & haruka email ba kliente",
    // Login
    login_strap_top: "KINOS · Sistema Jestaun Bilhete",
    login_headline_line1: "Bilhete tama.",
    login_headline_line2: "Solusaun sai.",
    login_tagline: "Plataforma helpdesk IT intelijente no konfiavel, kria ba organizasaun sira iha Timor-Leste. Jere pedidu apoiu, atribui insidente ba tékniku loos, monitoriza dezempeñu SLA, no husik AI pre-triage prioriza asuntu sira lalais.",
    login_strap_bottom: "KINOS Sistema Jestaun Bilhete — apoiu lalais liu, komunikasaun klaru liu, servisu di'ak liu.",
    login_signin: "Tama",
    login_access_workspace: "Asesu ba ita-nia fatin servisu",
    login_subtitle: "Kontinua ho ita-nia konta Google. Só email aprovadu ona maka bele asesu KINOS Helpdesk — husu admin atu tau ita uluk.",
    login_continue_google: "Kontinua ho Google",
    login_feature_ai: "Kategorizasaun & pre-triage prioridade automátiku ho AI",
    login_feature_sla: "Kontajen SLA iha oras servisu & alerta kuandu la kumpri",
    login_feature_roles: "Portál tuir papel · Kliente · Ajente · Xefe Ekipa · Admin",
    // Language switcher
    lang_switcher_label: "Lian",
    lang_en: "Inglés",
    lang_tet: "Tetun",
  },
};

const LanguageContext = createContext({
  lang: "en",
  setLang: () => {},
  t: (k) => k,
});

export function LanguageProvider({ children }) {
  const [lang, setLangState] = useState(() => {
    try {
      const stored = localStorage.getItem("kinos_lang");
      return stored === "tet" ? "tet" : "en";
    } catch { return "en"; }
  });

  useEffect(() => {
    try { localStorage.setItem("kinos_lang", lang); } catch { /* noop */ }
    document.documentElement.lang = lang === "tet" ? "tet" : "en";
  }, [lang]);

  const setLang = (l) => setLangState(l === "tet" ? "tet" : "en");

  const t = (key, vars) => {
    const dict = DICTS[lang] || DICTS.en;
    let s = dict[key] ?? DICTS.en[key] ?? key;
    if (vars) {
      for (const k of Object.keys(vars)) {
        s = s.replaceAll(`{${k}}`, vars[k]);
      }
    }
    return s;
  };

  return (
    <LanguageContext.Provider value={{ lang, setLang, t }}>
      {children}
    </LanguageContext.Provider>
  );
}

export const useT = () => useContext(LanguageContext);

// Very small Tetum-keyword heuristic used to decide whether to offer a
// "translate" preview for a user-facing message.
const TETUM_TOKENS = [
  " ha'u", " ha'u-nia", " nia ", " ita ", " la bele", " bele ", " labele", " lalais",
  " obrigadu", " diak ", " di'ak", " loron", " deit", " nune'e", " maka ", " husi ",
  " iha ", " ba ", " hato'o", " keta ", " tanba ", " karik ", " lae ", " sim ",
  " informasaun", " sistema", " problema", " komputador", " internet", " impresora",
  " Timor-Leste", " Timor Leste", " tetun", " tetum",
];
export function guessLang(text) {
  if (!text) return "en";
  const t = ` ${String(text).toLowerCase()} `;
  let hits = 0;
  for (const tok of TETUM_TOKENS) {
    if (t.includes(tok)) hits += 1;
    if (hits >= 2) return "tet";
  }
  return "en";
}

export function LanguageSwitcher({ compact = false }) {
  const { lang, setLang, t } = useT();
  const Btn = ({ code, label }) => (
    <button
      onClick={() => setLang(code)}
      data-testid={`lang-btn-${code}`}
      className={`px-2 py-1 text-[10px] font-mono uppercase tracking-[0.2em] transition-colors ${
        lang === code
          ? "bg-cyan-500/15 text-cyan-300 border-cyan-500/40"
          : "text-slate-400 border-slate-800 hover:text-white hover:border-slate-600"
      } border rounded-sm`}
    >
      {label}
    </button>
  );
  return (
    <div className="inline-flex items-center gap-1.5" data-testid="lang-switcher" title={t("lang_switcher_label")}>
      {!compact && (
        <span className="text-[9px] font-mono uppercase tracking-[0.25em] text-slate-600">{t("lang_switcher_label")}</span>
      )}
      <Btn code="en" label="EN" />
      <Btn code="tet" label="TET" />
    </div>
  );
}
